import { useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import StarRating from "@/components/base/StarRating";
import CharacterModal from "@/pages/plot/components/CharacterModal";
import useSeo from "@/hooks/useSeo";
import { characters, synopsis, stills } from "@/mocks/movie";
import { reviews } from "@/mocks/reviews";

export default function Plot() {
  useSeo({
    title: "激情丛林电影剧情介绍 - 深度剧情解析和角色介绍",
    description:
      "《激情丛林》完整剧情介绍：植物学家深入雨林寻找失落档案的故事脉络，主要角色林深、苏雨桐、阿岩等的介绍与深度解析，帮助影迷读懂影片主题。",
    keywords: "激情丛林剧情,激情丛林剧情介绍,角色介绍,电影解析,影片背景",
  });

  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState<null | (typeof characters)[number]>(null);

  const visibleSynopsis = expanded ? synopsis : synopsis.slice(0, 2);
  const relatedReviews = reviews.filter((r) => r.category === "剧情解析").slice(0, 3);

  return (
    <div className="w-full">
      <PageHero title="电影剧情介绍" crumbs={[{ label: "剧情介绍" }]} />

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
          <article id="synopsis" className="max-w-3xl">
            <SectionHeading eyebrow="Synopsis" title="故事梗概" />
            <div className="space-y-5 text-sm leading-8 text-foreground-300 md:text-[15px]">
              {visibleSynopsis.map((p) => (
                <p key={p.slice(0, 12)}>{p}</p>
              ))}
            </div>

            {!expanded && (
              <div className="pointer-events-none -mt-10 h-24 bg-gradient-to-t from-background-950 to-transparent"></div>
            )}

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              {expanded ? "收起剧情" : "展开完整剧情"}
            </button>
          </article>

          <aside className="space-y-5">
            <div className="panel rounded-lg p-6">
              <h3 className="font-heading text-base font-bold text-foreground-50">关键信息</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">片长</span>
                  <span className="text-foreground-100">138 分钟</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">类型</span>
                  <span className="text-foreground-100">冒险 / 剧情 / 悬疑</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">上映</span>
                  <span className="text-foreground-100">2024-10-18</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">片方评分</span>
                  <StarRating rating={4.6} showValue />
                </li>
              </ul>
              <p className="mt-5 border-t border-background-800 pt-4 text-xs leading-relaxed text-foreground-500">
                本文为演示内容，剧情与角色均为虚构设定，用于展示内容结构。
              </p>
            </div>

            <nav className="panel rounded-lg p-6" aria-label="页面内导航">
              <h3 className="font-heading text-base font-bold text-foreground-50">页内导航</h3>
              <ul className="mt-4 space-y-2.5 text-sm">
                {[
                  { href: "#synopsis", label: "故事梗概" },
                  { href: "#characters", label: "主要角色" },
                  { href: "#stills", label: "相关剧照" },
                  { href: "#reviews", label: "相关影评" },
                ].map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      className="flex items-center gap-2 text-foreground-400 transition-colors hover:text-accent-400"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        </div>
      </section>

      <section id="characters" className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="Characters"
          title="主要角色介绍"
          desc="点击角色卡片，查看该角色的完整介绍与关键特质。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {characters.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c)}
              className="panel group flex items-center gap-4 rounded-lg p-4 text-left transition-colors hover:border-primary-500"
            >
              <span className="h-20 w-20 shrink-0 overflow-hidden rounded-md">
                <img
                  src={c.avatar}
                  alt={`${c.name}（${c.actor} 饰）`}
                  title={`${c.name} - 激情丛林电影角色`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-label text-xs uppercase tracking-widest text-accent-500">
                  {c.role}
                </span>
                <span className="mt-1 block font-heading text-lg font-bold text-foreground-50">
                  {c.name}
                </span>
                <span className="mt-1 block truncate font-label text-xs text-foreground-500">
                  演员：{c.actor}
                </span>
              </span>
              <i className="ri-add-circle-line text-xl text-background-400 transition-colors group-hover:text-accent-400"></i>
            </button>
          ))}
        </div>
      </section>

      <section id="stills" className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading eyebrow="Stills" title="相关剧照" desc="影片中的代表性画面（演示素材）。" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {stills.slice(0, 3).map((s) => (
            <figure key={s.title} className="overflow-hidden rounded-lg border border-background-800">
              <div className="h-48 w-full overflow-hidden">
                <img
                  src={s.src}
                  alt={s.alt}
                  title={`${s.title} - 激情丛林电影剧照`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </div>
              <figcaption className="flex items-center gap-2 bg-background-900 px-4 py-3 font-label text-xs text-foreground-400">
                <i className="ri-image-line text-accent-500"></i>
                {s.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="reviews" className="mx-auto w-full px-4 py-12 md:px-6 md:py-14">
        <SectionHeading
          eyebrow="Related Reviews"
          title="相关影评推荐"
          desc="从深度解析的角度，延伸阅读影片的叙事与人物。"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {relatedReviews.map((r) => (
            <Link
              key={r.id}
              to={`/review/${r.id}`}
              className="panel group flex h-full flex-col rounded-lg p-5 transition-colors hover:border-primary-500"
            >
              <span className="font-label text-xs text-accent-500">{r.category}</span>
              <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-50 group-hover:text-accent-400">
                {r.title}
              </h3>
              <p className="mt-2 line-clamp-2-custom text-sm text-foreground-400">{r.excerpt}</p>
              <div className="mt-auto flex items-center justify-between pt-4">
                <StarRating rating={r.rating} showValue />
                <span className="font-label text-xs text-foreground-500">{r.date}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CharacterModal character={active} onClose={() => setActive(null)} />
    </div>
  );
}